/**
 * External Imports
 */
import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { connect } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuImage, NeuInput } from '@neutron/react';

/**
 * Internal Imports
 */
import Loading from '../../components/shared/Loading';
import { RootState } from '../../redux/store';
import { handleLastPath } from '../../utils/lastPath';

/**
 * Global Type Definition Imports
 */
import { AuthUserProps } from '../../config/interfaces';

/**
 * Style Imports
 */
import { BrandLabel, LoginContainer, LoginForm } from './Login.styles';

/**
 * Static File Imports
 */
import hcaStackedLogo from '../../assets/images/HCA_stacked.jpg';
import orbitLogoOrangeWithName from '../../assets/images/orbit_orange_logo.png';

interface ILoginProps {
  authorizedUser: AuthUserProps;
  authRoles: string[];
}

const Login: FC<ILoginProps> = ({ authorizedUser, authRoles }) => {
  const { isAuthenticated, signinRedirect } = useAuth();

  const [login34, setLogin34] = useState('');
  const { pathname, search } = useLocation();

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');

  const navigate = useNavigate();

  const onChangeEvent = (e: any) => {
    const { value } = e.target as HTMLInputElement;
    setLogin34(value);
  };

  const handleAuthentication = () => {
    if (isAuthenticated) {
      return handleLastPath(authRoles, navigate);
    }
    if (process.env.REACT_APP_ENV !== 'production')
      window.localStorage.setItem('34', login34);
    console.log('Authorizing user...');
    return signinRedirect();
  };

  useEffect(() => {
    if (pathname.includes('task/complete')) {
      window.sessionStorage.setItem(
        'task',
        new URLSearchParams(search).get('i') ?? ''
      );
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (Object.keys(authorizedUser).length > 0) {
      const storedDeepLinkTaskId = sessionStorage.getItem('task');
      if (storedDeepLinkTaskId) {
        navigate(`/task/complete?i=${storedDeepLinkTaskId}`);
      } else {
        handleLastPath(authRoles, navigate);
      }
    }
  }, [authorizedUser]);

  return process.env.REACT_APP_ENV !== 'production' &&
    !code &&
    !isAuthenticated &&
    Object.keys(authorizedUser).length === 0 ? (
    <LoginContainer id="Login-page" className="w-100">
      <NeuImage
        id="Orbit-Logo"
        className="position-absolute ms-5 mt-5"
        alt="orbit-logo-orange"
        src={hcaStackedLogo}
      />
      <div className="d-flex justify-content-center align-items-center h-100 ">
        <LoginForm>
          <div className="d-md-flex justify-content-center align-items-center mb-4">
            <NeuImage alt="orbit-logo-orange" src={orbitLogoOrangeWithName} />
            <BrandLabel className="neu-text-primary-100 ms-2 mb-0">
              Orbit
            </BrandLabel>
          </div>
          <NeuInput
            id="Login-Input-Box"
            data-testid="login-text-input"
            className="mt-1"
            label="3/4 ID"
            required
            value={login34}
            onNeuChange={e => {
              onChangeEvent(e);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleAuthentication();
              }
            }}
          />
          <br />
          <NeuButton
            id="Login-Button"
            data-testid="login-button"
            className="w-100"
            kind="primary"
            size="small"
            onClick={() => {
              handleAuthentication();
            }}
          >
            Login
          </NeuButton>
        </LoginForm>
      </div>
    </LoginContainer>
  ) : (
    <Loading centered label="api" />
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authorizedUser: state.AuthorizedUser.authorizedUser,
  authRoles: state.AuthorizedUser.authRoles
});

export default connect(mapReduxStateToProps)(Login);
